import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { saleAPI } from "../../services/saleAPI";

export type RefundSaleDialogProps = {
  open: boolean;
  sale: {
    id: string;
    saleNumber?: string;
    netAmount: number;
    status?: string;
  } | null;
  onClose: () => void;
  onSuccess: () => void;
};

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export default function RefundSaleDialog({
  open,
  sale,
  onClose,
  onSuccess,
}: RefundSaleDialogProps) {
  const [reason, setReason] = useState("");
  const [refundAmount, setRefundAmount] = useState<number | "">("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && sale) {
      setReason("");
      setRefundAmount(sale.netAmount || 0);
      setError("");
    }
  }, [open, sale]);

  if (!sale) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      setError("Please enter a return/refund reason (e.g. Expired medicine, Customer return, etc.)");
      return;
    }
    const amt = typeof refundAmount === "number" ? refundAmount : Number(refundAmount);
    if (!amt || amt <= 0) {
      setError("Please enter a valid refund amount");
      return;
    }
    if (amt > Number(sale!.netAmount)) {
      setError(`Refund amount cannot exceed bill amount (${formatCurrency(sale!.netAmount)})`);
      return;
    }

    setBusy(true);
    try {
      await saleAPI.refundSale(sale!.id, {
        reason: trimmedReason,
        refundAmount: amt,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Refund failed"
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Return / Refund Sale</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Bill: <strong>{sale.saleNumber || sale.id.slice(0, 8)}</strong> · Total:{" "}
              {formatCurrency(sale.netAmount)}
            </Typography>
            <Alert severity="info">
              A Credit Note will be generated for this return. Stock & inventory will be
              restored back to the store shelf.
            </Alert>
            <TextField
              label="Refund Amount (₹)"
              type="number"
              value={refundAmount}
              onChange={(e) =>
                setRefundAmount(e.target.value === "" ? "" : Number(e.target.value))
              }
              fullWidth
              size="small"
              required
              disabled={busy}
              inputProps={{ min: 1, max: sale.netAmount, step: "0.01" }}
            />
            <TextField
              label="Return Reason (e.g. Customer return, Defective, Cancelled)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
              required
              disabled={busy}
              placeholder="e.g. Customer return / exchange, order cancelled"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            disabled={busy}
          >
            {busy ? "Processing Return…" : "Confirm Return"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
